import { useEffect } from 'react';

interface Props {
  open: boolean;
  side: 'left' | 'right' | 'bottom';
  label: string;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * An overlay pane for screens with no room for a column.
 *
 * The same components render inside it as in the desktop columns — this only
 * changes where they sit, never what they are, so a fix never has to be made
 * twice.
 */
export function Sheet({ open, side, label, onClose, children }: Props) {
  // Escape closes, and the page behind must not scroll while a sheet is over it.
  useEffect(() => {
    if (!open) return;

    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  if (!open) return null;

  const panel = {
    left: 'inset-y-0 left-0 w-[min(19rem,85vw)] border-r',
    right: 'inset-y-0 right-0 w-[min(20rem,88vw)] border-l',
    bottom: 'inset-x-0 bottom-0 max-h-[85vh] rounded-t-xl border-t',
  }[side];

  return (
    <div className="fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label={label}>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div className={`absolute ${panel} min-h-0 border-(--color-line) bg-(--color-surface) shadow-xl`}>
        <div className="h-full min-h-0 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}
